/**
 * RitualCourseDays Component
 * Displays course day structure with locked/unlocked/completed states
 * and expandable step content
 */

import { ChevronDown, ChevronUp, Lock, Play } from "lucide-react"
import { useState } from "react"
import { cn } from "@/lib"
import type { CourseDay } from "../../types/detail.types"

interface RitualCourseDaysProps {
  days: CourseDay[]
  defaultOpen?: boolean
}

export function RitualCourseDays({ days, defaultOpen = true }: RitualCourseDaysProps) {
  const [expandedDay, setExpandedDay] = useState<number | null>(null)

  const dayWithSteps = days.find(d => d.steps && d.steps.length > 0)
  const initialExpanded = defaultOpen ? (dayWithSteps?.dayNumber ?? null) : null

  const toggleDay = (dayNumber: number) => {
    setExpandedDay(prev => (prev === dayNumber ? null : dayNumber))
  }

  // Use initialExpanded on first render
  const currentExpanded = expandedDay ?? initialExpanded

  return (
    <div className="border border-gray/30 rounded-xl overflow-hidden bg-white">
      <div className="px-5 py-4">
        <span className="text-sm font-semibold text-primary">Explore Course</span>
      </div>

      <div className="px-5 pb-4 space-y-1">
        {days.map(day => {
          const isExpanded = currentExpanded === day.dayNumber
          const hasSteps = day.steps && day.steps.length > 0

          return (
            <div
              key={day.dayNumber}
              className={cn(
                "rounded-lg transition-colors",
                isExpanded && hasSteps ? "bg-beige-light p-4" : "",
              )}
            >
              <button
                type="button"
                onClick={() => (day.isUnlocked ? toggleDay(day.dayNumber) : undefined)}
                className={cn(
                  "w-full flex items-center gap-3 text-left",
                  !isExpanded || !hasSteps ? "py-3" : "",
                  !day.isUnlocked && "opacity-70 cursor-default",
                )}
                disabled={!day.isUnlocked}
              >
                {/* Icon */}
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  {day.isCompleted ? (
                    <div className="w-8 h-8 rounded-full bg-beige-medium flex items-center justify-center">
                      <Play className="w-3.5 h-3.5 text-primary ml-0.5" fill="currentColor" />
                    </div>
                  ) : day.isUnlocked ? (
                    <div className="w-8 h-8 rounded-full bg-beige-medium flex items-center justify-center">
                      <Play className="w-3.5 h-3.5 text-primary ml-0.5" fill="currentColor" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-beige-medium flex items-center justify-center">
                      <Lock className="w-3.5 h-3.5 text-stone" />
                    </div>
                  )}
                </div>

                {/* Day info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-primary">{day.title}</p>
                  <p className="text-xs text-stone">{day.subtitle}</p>
                </div>

                {/* Expand/Collapse arrow */}
                {day.isUnlocked && (
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-primary" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-primary" />
                    )}
                  </div>
                )}
              </button>

              {/* Expanded steps content */}
              {isExpanded && hasSteps && (
                <div className="mt-3 space-y-3">
                  {day.steps?.map(step => (
                    <div key={step.number} className="text-sm">
                      <span className="text-primary">
                        <span className="font-bold">{step.number}. </span>
                        <span className="font-bold">{step.title}.</span>{" "}
                        <span className="text-stone">{step.description}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
